import { type HttpErrorResponse } from '@angular/common/http';
import { type Document as ImageDocument } from '@api/locations';
import { EntityStatus } from '@core';
import { createEntityAdapter, type EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';

import { locationUtils } from '../helpers';
import { type FloorSpaceModel, type LocationData, type LocationFullData, type RoomSpaceModel } from '../models';
import { LocationActions } from './locations.actions';

export interface LocationsState extends EntityState<LocationData> {
  allLocationsLoaded: boolean;
  allLocationsLoading: boolean;
  areRoomDocumentsLoading: boolean;
  floorError: null | HttpErrorResponse;
  floorLoading: boolean;
  floorplanError: null | HttpErrorResponse;
  floorplanLoading: boolean;
  floorUpdateLoading: boolean;
  isFullLocationLoaded: boolean;
  isLocationLoading: boolean;
  isLocationsSummariesLoading: boolean;
  locationError: null | HttpErrorResponse;
  locationState: LocationFullData;
  roomDocuments: ImageDocument[];
  roomError: null | HttpErrorResponse;
  roomLoading: boolean;
  roomplanError: null | HttpErrorResponse;
  roomplanLoading: boolean;
  roomUpdateLoading: boolean;
}

export const locationAdapter = createEntityAdapter<LocationData>({});

export const initialLocationsState: LocationsState = locationAdapter.getInitialState({
  allLocationsLoaded: false,
  allLocationsLoading: false,
  areRoomDocumentsLoading: false,
  floorError: null,
  floorLoading: false,
  floorUpdateLoading: false,
  floorplanError: null,
  floorplanLoading: false,
  isFullLocationLoaded: false,
  isLocationLoading: false,
  isLocationsSummariesLoading: false,
  locationError: null,
  locationState: {} as LocationFullData,
  roomDocuments: [],
  roomError: null,
  roomLoading: false,
  roomUpdateLoading: false,
  roomplanError: null,
  roomplanLoading: false,
});

export const locationsReducer = createReducer(
  initialLocationsState,
  on(LocationActions.loadLocations, state => {
    return {
      ...state,
      allLocationsLoading: false,
    };
  }),
  on(LocationActions.locationsLoaded, (state, action) => {
    return locationAdapter.setAll(action.location, {
      ...state,
      allLocationsLoaded: true,
      allLocationsLoading: false,
      isLocationsSummariesLoading: true,
      locationState: {} as LocationFullData,
    });
  }),
  on(LocationActions.loadLocationsSummariesSuccess, (state, action) => {
    return locationAdapter.updateMany(action.data, {
      ...state,
      isLocationsSummariesLoading: false,
    });
  }),
  on(LocationActions.loadLocationsSummariesError, state => {
    return { ...state, isLocationsSummariesLoading: false };
  }),
  on(LocationActions.addLocation, state => ({ ...state, isLocationLoading: true, locationError: null })),
  on(LocationActions.addLocationSuccess, (state, action) =>
    locationAdapter.addOne(action.newLocation, {
      ...state,
      isLocationLoading: false,
      locationState: { ...action.newLocation, floors: [] },
    }),
  ),

  on(LocationActions.updateLocation, state => ({ ...state, isLocationLoading: true, locationError: null })),
  on(LocationActions.locationError, (state, action) => ({
    ...state,
    isLocationLoading: false,
    locationError: action.locationError,
  })),
  on(LocationActions.updateLocationSuccess, (state, action) => {
    return locationAdapter.updateOne(
      {
        changes: action.location,
        id: action.location.id,
      },
      {
        ...state,
        isLocationLoading: false,
        locationState: Object.keys(state.locationState).length
          ? { ...action.location, floors: [...state.locationState.floors] }
          : ({} as LocationFullData),
      },
    );
  }),
  on(LocationActions.getLocationState, state => ({
    ...state,
    isFullLocationLoaded: false,
    isLocationLoading: true,
  })),
  on(LocationActions.getLocationStateSuccess, (state, action) => {
    return {
      ...state,
      isFullLocationLoaded: true,
      isLocationLoading: false,
      locationState: action.locationState,
    };
  }),
  on(LocationActions.resetLocationState, state => {
    return {
      ...state,
      locationState: {} as LocationFullData,
    };
  }),
  on(LocationActions.resetDefaultLocationsState, () => {
    return {
      ...initialLocationsState,
    };
  }),
  on(LocationActions.deleteLocation, (state, action) => {
    return locationAdapter.removeOne(
      action.locationId,
      {
        ...state,
        locationState: {} as LocationFullData,
      },
    );
  }),
  // Handle Floor actions
  on(LocationActions.updateFloor, state => {
    return {
      ...state,
      floorError: null,
      floorUpdateLoading: true,
    };
  }),
  on(LocationActions.updateFloorSuccess, (state, action) => {
    const floor = action.floor as FloorSpaceModel;


    return {
      ...state,
      floorError: null,
      floorUpdateLoading: false,
      locationState: {
        ...state.locationState,
        floors: state.locationState.floors.map(f => {
          const rooms = f.rooms?.map(room => ({
            ...room,
            parentSpace: floor,
            status: floor.status === EntityStatus.Archived
              ? EntityStatus.Archived
              : room.status,
          })).filter(locationUtils.isRoom);

          return f.id === floor.id
            ? { ...f, ...floor, rooms }
            : f;
        }).filter(locationUtils.isFloor),
      },
    };
  }),
  on(LocationActions.updateFloorError, (state, action) => {
    return {
      ...state,
      floorError: action.error,
      floorUpdateLoading: false,
    };
  }),
  on(LocationActions.addFloor, state => {
    return {
      ...state,
      floorError: null,
      floorLoading: true,
    };
  }),
  on(LocationActions.addFloorSuccess, (state, action) => {
    const floor = action.floor as FloorSpaceModel;


    return {
      ...state,
      floorError: null,
      floorLoading: false,
      locationState: { ...state.locationState, floors: [...state.locationState.floors, floor] },
    };
  }),
  on(LocationActions.addFloorError, (state, action) => {
    return {
      ...state,
      floorError: action.error,
      floorLoading: false,
    };
  }),
  on(LocationActions.dropFloor, (state, action) => {
    return {
      ...state,
      locationState: {
        ...state.locationState,
        floors: state.locationState.floors.filter(floor => floor.id !== action.floorId),
      },
    };
  }),
  on(LocationActions.reorderFloors, (state, action) => {
    return {
      ...state,
      locationState: {
        ...state.locationState,
        floors: action.floors.map(floor => {
          const originalFloor = state.locationState.floors.find(f => f.id === floor.id);

          return { ...originalFloor, ...floor } as FloorSpaceModel;
        }),
      },
    };
  }),
  // Handle Room actions
  on(LocationActions.addRoom, state => {
    return {
      ...state,
      roomError: null,
      roomLoading: true,
    };
  }),
  on(LocationActions.addRoomSuccess, (state, action) => {
    const room = action.room as RoomSpaceModel;

    return {
      ...state,
      locationState: {
        ...state.locationState,
        floors: state.locationState.floors.map(floor => {
          if (floor.id === room.parentSpaceId) {
            return {
              ...floor,
              rooms: floor.rooms ? [...floor.rooms, room] : [room],
            };
          }

          return floor;
        }),
      },
      roomError: null,
      roomLoading: false,
    };
  }),
  on(LocationActions.addRoomError, (state, action) => {
    return {
      ...state,
      roomError: action.error,
      roomLoading: false,
    };
  }),
  on(LocationActions.updateRoom, state => {
    return {
      ...state,
      roomError: null,
      roomUpdateLoading: true,
    };
  }),
  on(LocationActions.updateRoomSuccess, (state, action) => {
    const room = action.room as RoomSpaceModel;
    const floorSummary = action.floorSummary;

    return {
      ...state,
      locationState: {
        ...state.locationState,
        floors: state.locationState.floors.map(floor => {
          const isUpdatedRoom = floor.id === room.parentSpaceId || floor.rooms?.some(({ id }) => id === room.id);

          if (isUpdatedRoom) {
            return {
              ...floor,
              devicesByStatuses: floorSummary
                ? {
                  ...floor.devicesByStatuses,
                  activeCount: floorSummary.devicesByStatuses.activeCount,
                  pausedCount: floorSummary.devicesByStatuses.pausedCount,
                }
                : floor.devicesByStatuses,
              rooms: floor.rooms
                ? floor.rooms.map((r: RoomSpaceModel) => {
                  return r.id === room.id ? { ...r, ...room } : r;
                }).filter(locationUtils.isRoom)
                : [],
            };
          }

          return floor;
        }),
      },
      roomError: null,
      roomUpdateLoading: false,
    };
  }),
  on(LocationActions.updateRoomError, (state, action) => {
    return {
      ...state,
      roomError: action.error,
      roomUpdateLoading: false,
    };
  }),
  on(LocationActions.uploadPlan, state => {
    return {
      ...state,
      roomplanError: null,
      roomplanLoading: true,
    };
  }),
  on(LocationActions.uploadPlanSuccess, (state, action) => {
    return {
      ...state,
      roomDocuments: [...state.roomDocuments, action.document],
      roomplanLoading: false,
    };
  }),
  on(LocationActions.uploadPlanError, (state, action) => {
    return {
      ...state,
      roomplanError: action.error,
      roomplanLoading: false,
    };
  }),
  on(LocationActions.deleteRoomplan, state => {
    return {
      ...state,
      roomplanError: null,
      roomplanLoading: true,
    };
  }),
  on(LocationActions.deleteRoomplanSuccess, (state, action) => {
    return {
      ...state,
      roomDocuments: state.roomDocuments.filter(({ fileName }) => action.fileName !== fileName),
      roomplanLoading: false,
    };
  }),
  on(LocationActions.deleteRoomplanError, (state, action) => {
    return {
      ...state,
      roomplanError: action.error,
      roomplanLoading: false,
    };
  }),
  on(LocationActions.getSpaceDocuments, state => {
    return {
      ...state,
      areRoomDocumentsLoading: true,
      roomDocuments: [],
    };
  }),
  on(LocationActions.getSpaceDocumentsError, state => {
    return {
      ...state,
      areRoomDocumentsLoading: false,
    };
  }),
  on(LocationActions.getSpaceDocumentsSuccess, (state, action) => {
    return {
      ...state,
      areRoomDocumentsLoading: false,
      roomDocuments: action.documents,
    };
  }),

  on(LocationActions.dropRoom, (state, action) => {
    return {
      ...state,
      locationState: {
        ...state.locationState,
        floors: state.locationState.floors.map(floor => {
          if (floor.id === action.floorId) {
            return {
              ...floor,
              rooms: floor.rooms ? floor.rooms.filter((room) => room.id !== action.roomId) : [],
            };
          }

          return floor;
        }),
      },
    };
  }),

  // Floorpan actions
  on(LocationActions.uploadFloorplan, state => {
    return {
      ...state,
      floorplanError: null,
      floorplanLoading: true,
    };
  }),
  on(LocationActions.uploadFloorplanSuccess, (state, action) => {
    return {
      ...state,
      floorplanLoading: false,
      locationState: {
        ...state.locationState,
        floors: state.locationState.floors.map(floor => {
          return floor.id === action.spaceId ? { ...floor, documentReference: action.document.fileName } : floor;
        }),
      },
    };
  }),
  on(LocationActions.uploadFloorplanError, (state, action) => {
    return {
      ...state,
      floorplanError: action.error,
      floorplanLoading: false,
    };
  }),
  on(LocationActions.deleteFloorplan, state => {
    return {
      ...state,
      floorplanError: null,
      floorplanLoading: true,
    };
  }),
  on(LocationActions.deleteFloorplanSuccess, (state, action) => {
    return {
      ...state,
      floorplanLoading: false,
      locationState: {
        ...state.locationState,
        floors: state.locationState.floors.map(floor => {
          return floor.id === action.spaceId ? { ...floor, documentReference: '' } : floor;
        }),
      },
    };
  }),
  on(LocationActions.deleteFloorplanError, (state, action) => {
    return {
      ...state,
      floorplanError: action.error,
      floorplanLoading: false,
    };
  }),
);
